import { useEffect, useState } from "react";
import { Loader2, Check } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const SESS = [2, 3, 4, 5, 6];
const STD = [30, 45, 60, 75, 90];
const LONG = [45, 60, 75, 90, 120, 150];

export function TimeVolumeCard({ onSaved, autoLoad = true }) {
  const [v, setV] = useState({ sessions_per_week: 3, max_session_min: 45, max_long_run_min: 90 });
  const [loading, setLoading] = useState(autoLoad);
  const [saving, setSaving] = useState(false);
  const [migrated, setMigrated] = useState(false);

  useEffect(() => {
    if (!autoLoad) return;
    api.get("/profile/time-volume").then((r) => {
      const std = r.data.max_session_min || 45;
      let long = r.data.max_long_run_min || 90;
      if (long < std) long = std;
      setV({ sessions_per_week: r.data.sessions_per_week || 3, max_session_min: std, max_long_run_min: long });
      setMigrated(!!r.data.needs_confirmation);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [autoLoad]);

  const pickStd = (n) => setV((prev) => ({ ...prev, max_session_min: n, max_long_run_min: Math.max(prev.max_long_run_min, n) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/profile/time-volume", v);
      toast.success("Disponibilités enregistrées");
      onSaved && onSaved(v);
    } catch { toast.error("Enregistrement impossible, réessaie."); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-sage" /></div>;

  const Chip = ({ active, onClick, children, tid }) => (
    <button type="button" data-testid={tid} onClick={onClick}
      className={`rounded-full px-4 py-2 text-sm border transition-colors ${active ? "bg-obsidian text-white border-obsidian" : "border-border text-obsidian/70 hover:bg-sage/10"}`}>{children}</button>
  );

  const longOptions = LONG.filter((n) => n >= v.max_session_min);

  return (
    <div className="space-y-6" data-testid="time-volume-card">
      {migrated && (
        <p className="text-xs text-obsidian/50 bg-sage/5 border border-border rounded-lg px-3 py-2" data-testid="tv-migrated-note">
          Nous avons pré-rempli ces valeurs à partir de ton ancien profil. Confirme-les ou ajuste-les.
        </p>
      )}
      <div>
        <div className="text-sm font-medium">Combien de séances de course par semaine ?</div>
        <div className="mt-2 flex flex-wrap gap-2">
          {SESS.map((n) => <Chip key={n} tid={`tv-sessions-${n}`} active={v.sessions_per_week === n} onClick={() => setV({ ...v, sessions_per_week: n })}>{n}</Chip>)}
        </div>
      </div>
      <div>
        <div className="text-sm font-medium">Durée maximale d'une séance classique ?</div>
        <div className="mt-2 flex flex-wrap gap-2">
          {STD.map((n) => <Chip key={n} tid={`tv-std-${n}`} active={v.max_session_min === n} onClick={() => pickStd(n)}>{n} min</Chip>)}
        </div>
      </div>
      <div>
        <div className="text-sm font-medium">Durée maximale d'une sortie longue ?</div>
        <div className="mt-2 flex flex-wrap gap-2">
          {longOptions.map((n) => <Chip key={n} tid={`tv-long-${n}`} active={v.max_long_run_min === n} onClick={() => setV({ ...v, max_long_run_min: n })}>{n >= 120 ? `${n / 60} h` : `${n} min`}</Chip>)}
        </div>
        <p className="mt-2 text-xs text-obsidian/45" data-testid="tv-long-hint">La sortie longue est toujours au moins aussi longue que ta séance classique.</p>
      </div>
      <p className="text-xs text-obsidian/45">Plus besoin de fixer des jours précis : tes séances sont numérotées dans l'ordre et MAI t'explique quand faire chacune. Tu peux aussi déclarer tes autres sports dans ton profil.</p>
      <button data-testid="tv-save-button" onClick={save} disabled={saving}
        className="w-full min-h-[48px] rounded-xl bg-obsidian text-white text-sm font-medium inline-flex items-center justify-center gap-1.5 disabled:opacity-40 active:scale-[0.99] transition-transform">
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Enregistrer
      </button>
    </div>
  );
}
